import { useEffect, useId, useState } from "react";
import clsx from "clsx";
import { useLocale } from "@/i18n";

const DEFAULT_MAX_QUICK = 4;

function parseCount(value: string): number {
  const n = Number(value);
  if (!Number.isFinite(n) || n < 0) return 0;
  return Math.floor(n);
}

export interface PartyCountFieldProps {
  label: string;
  hint?: string;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  minCount?: number;
  maxQuickSelect?: number;
  className?: string;
}

export function PartyCountField({
  label,
  hint,
  value,
  onChange,
  disabled = false,
  minCount = 0,
  maxQuickSelect = DEFAULT_MAX_QUICK,
  className,
}: PartyCountFieldProps) {
  const { messages: m, locale } = useLocale();
  const inputId = useId();
  const count = parseCount(value);
  const [manualMode, setManualMode] = useState(count > maxQuickSelect);

  useEffect(() => {
    if (count > maxQuickSelect) setManualMode(true);
  }, [count, maxQuickSelect]);

  const showManual = manualMode || count > maxQuickSelect;
  const quickOptions = Array.from(
    { length: maxQuickSelect - minCount + 1 },
    (_, i) => minCount + i,
  );
  const numberLocale = locale === "en" ? "en-GB" : "de-DE";

  function selectQuick(n: number) {
    setManualMode(false);
    onChange(String(n));
  }

  function openManual() {
    setManualMode(true);
    if (count <= maxQuickSelect) {
      onChange(String(maxQuickSelect + 1));
    }
  }

  function onManualChange(raw: string) {
    if (raw === "") {
      onChange("");
      return;
    }
    const digits = raw.replace(/\D/g, "");
    onChange(digits);
  }

  return (
    <div className={clsx("text-sm", className)}>
      <span className="block font-medium text-slate-800">
        {label}
        {hint ? <span className="ml-1.5 text-xs font-normal text-slate-500">{hint}</span> : null}
      </span>
      <div className="mt-1.5 flex flex-wrap gap-1.5" role="group" aria-label={label}>
        {quickOptions.map((n) => {
          const selected = !showManual && count === n;
          return (
            <button
              key={n}
              type="button"
              disabled={disabled}
              aria-pressed={selected}
              onClick={() => selectQuick(n)}
              className={clsx(
                "bk-count-btn",
                selected ? "bk-count-btn--selected" : "bk-count-btn--idle",
                disabled && "cursor-not-allowed opacity-60",
              )}
            >
              {n.toLocaleString(numberLocale)}
            </button>
          );
        })}
        {!showManual ? (
          <button
            type="button"
            disabled={disabled}
            onClick={openManual}
            className={clsx(
              "bk-count-btn bk-count-btn--more",
              disabled && "cursor-not-allowed opacity-60",
            )}
          >
            {`${(maxQuickSelect + 1).toLocaleString(numberLocale)}+`}
          </button>
        ) : null}
      </div>
      {showManual ? (
        <div className="mt-2 space-y-1">
          <label htmlFor={inputId} className="sr-only">
            {label} (manuell)
          </label>
          <input
            id={inputId}
            type="number"
            min={maxQuickSelect + 1}
            className="bk-input w-full"
            value={value}
            onChange={(e) => onManualChange(e.target.value)}
            disabled={disabled}
            inputMode="numeric"
          />
          {!disabled ? (
            <button
              type="button"
              className="text-xs font-medium text-brand-700 hover:underline"
              onClick={() => {
                setManualMode(false);
                onChange(String(Math.min(Math.max(count, minCount), maxQuickSelect)));
              }}
            >
              {m.flow.quickSelect(
                minCount.toLocaleString(numberLocale),
                maxQuickSelect.toLocaleString(numberLocale),
              )}
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
